import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  Users,
  Download,
  Target,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Phone,
  BarChart3,
  MessageSquare,
  Smartphone,
  Calendar,
} from "lucide-react";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  getCampaignAnalyticsSummary,
  getCampaignDateWiseAnalytics,
  getCampaignContactsByStatus,
  exportCampaignContactsByStatus,
  recomputeCampaignAnalytics,
} from "../apis/campaign";
import { toast } from "react-toastify";
import TemplatePreview from "../components/templateCreator/TemplatePreview";
import { transformDuplicateTemplateToCreatorState } from "../utils/templateDataTransformers";
import { applyCampaignVarsToPreviewState } from "../utils/campaignTemplatePreview";

const STATUS_CONFIG = {
  overview: { label: "Overview", subLabel: "All buckets", color: "#1f2937", bgColor: "bg-gray-100", textColor: "text-gray-700", icon: BarChart3 },
  sent: { label: "Sent", subLabel: "in flight", color: "#3b82f6", bgColor: "bg-blue-50", textColor: "text-blue-700", icon: Send },
  delivered: { label: "Delivered", subLabel: "delivered, not read", color: "#10b981", bgColor: "bg-emerald-50", textColor: "text-emerald-700", icon: CheckCircle },
  read: { label: "Read", subLabel: "read, no reply", color: "#8b5cf6", bgColor: "bg-purple-50", textColor: "text-purple-700", icon: Eye },
  replied: { label: "Replied", subLabel: "replied", color: "#f59e0b", bgColor: "bg-amber-50", textColor: "text-amber-700", icon: MessageSquare },
  failed: { label: "Failed", subLabel: "failed (terminal)", color: "#ef4444", bgColor: "bg-red-50", textColor: "text-red-700", icon: XCircle },
  pending: { label: "Pending", subLabel: "not sent yet", color: "#6b7280", bgColor: "bg-gray-50", textColor: "text-gray-700", icon: Clock },
};

// Each outbound message lives in exactly one of these mutually-exclusive
// "farthest state" buckets, so the equation always holds:
//   pending + sent + delivered + read + replied + failed = total_contacts
// That's what the reconciliation strip below visualises.

function syncChartWithSnapshot(rows, analytics) {
  if (!analytics || !rows?.length) return rows || [];
  const point = {
    sent: Math.max(0, analytics.status_counts?.sent ?? 0),
    delivered: analytics.status_counts?.delivered || 0,
    read: analytics.status_counts?.read || 0,
    failed: analytics.status_counts?.failed || 0,
    replied: analytics.reply_count || 0,
  };
  const updated = [...rows];
  updated[updated.length - 1] = { ...updated[updated.length - 1], ...point };
  return updated;
}

const CampaignAnalytics = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [summaryData, setSummaryData] = useState(null);
  const [dateWiseData, setDateWiseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

  // Template preview carousel
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);

  useEffect(() => {
    loadSummary();
  }, [campaignId]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await getCampaignAnalyticsSummary(campaignId);
      if (res.data.success) {
        setSummaryData(res.data.data);
        await loadGraphData(res.data.data?.analytics);
      }
    } catch (error) {
      console.error("Error loading summary:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Force-recompute: drops the 30s snapshot cache server-side and re-reads
  // straight from whatsapp_messages. Used when the user wants up-to-the-second
  // numbers without waiting for the TTL.
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await recomputeCampaignAnalytics(campaignId);
      setDateWiseData([]);
      await loadSummary();
      // Reload the active tab list so its total agrees with the new card count
      if (activeTab && activeTab !== "overview") {
        await loadContacts(activeTab, pagination.page || 1);
      }
      toast.success("Analytics refreshed");
    } catch (error) {
      console.error("Error refreshing:", error);
      // Fall back to a plain summary reload (no recompute) so users still get a refresh
      await loadSummary();
    } finally {
      setRefreshing(false);
    }
  };

  const loadGraphData = async (analyticsOverride) => {
    try {
      setGraphLoading(true);
      const res = await getCampaignDateWiseAnalytics(campaignId, 30);
      if (res.data.success) {
        const raw = (res.data.data || []).map((d) => ({
          ...d,
          sent: Math.max(0, d.sent ?? 0),
        }));
        const analytics = analyticsOverride || summaryData?.analytics;
        setDateWiseData(syncChartWithSnapshot(raw, analytics));
      }
    } catch (error) {
      console.error("Error loading graph:", error);
    } finally {
      setGraphLoading(false);
    }
  };

  const loadContacts = useCallback(async (status, page = 1) => {
    if (!status || status === "overview") return;

    try {
      setContactsLoading(true);
      const res = await getCampaignContactsByStatus(campaignId, status, page, 50);
      if (res.data.success) {
        setContacts(res.data.data || []);
        setPagination({
          page: res.data.pagination.page,
          limit: res.data.pagination.limit,
          total: res.data.pagination.total,
          totalPages: res.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error("Error loading contacts:", error);
      toast.error("Failed to load contacts");
    } finally {
      setContactsLoading(false);
    }
  }, [campaignId]);

  const handleTabClick = (status) => {
    setActiveTab(status);
    if (status !== "overview") {
      setContacts([]);
      setPagination({ page: 1, limit: 50, total: 0, totalPages: 0 });
      loadContacts(status, 1);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadContacts(activeTab, newPage);
    }
  };

  const handleExport = async () => {
    if (!activeTab || activeTab === "overview") return;
    try {
      setExportLoading(true);
      const res = await exportCampaignContactsByStatus(campaignId, activeTab);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `campaign_${campaignId}_${activeTab}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Exported successfully");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleRetarget = () => {
    navigate(`/campaigns/${campaignId}/retarget?filter=${activeTab}`);
  };

  const getCount = (status) => {
    if (!summaryData?.analytics) return 0;
    if (status === "overview") return summaryData.analytics.total_contacts || 0;
    if (status === "pending") return summaryData.analytics.pending_contacts || 0;
    if (status === "replied") return summaryData.analytics.reply_count || 0;
    const v = summaryData.analytics.status_counts?.[status] ?? 0;
    if (status === "sent" && typeof v === "number" && v < 0) return 0;
    return v;
  };

  const getPercentage = (status) => {
    const total = summaryData?.analytics?.total_contacts || 0;
    if (total === 0) return 0;
    return Math.round((getCount(status) / total) * 100);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const fmt = (n) => Number(n || 0).toLocaleString();
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 min-w-[160px]">
          <p className="text-xs font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-xs py-0.5">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="text-gray-600">{entry.name}</span>
              </div>
              <span className="font-semibold tabular-nums" style={{ color: entry.color }}>
                {fmt(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const statusBadgeClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "completed") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (s === "active") return "bg-blue-50 text-blue-700 border-blue-200";
    if (s === "failed" || s === "aborted") return "bg-red-50 text-red-700 border-red-200";
    if (s === "scheduled") return "bg-violet-50 text-violet-700 border-violet-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  const metricCards = ["sent", "delivered", "read", "replied", "failed"];

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-gray-200 border-t-gray-900 rounded-full" />
      </div>
    );
  }

  const { campaign, templates, phone, analytics } = summaryData || {};
  const currentTemplate = templates?.[activeTemplateIndex];
  const previewState = currentTemplate
    ? applyCampaignVarsToPreviewState(
        transformDuplicateTemplateToCreatorState(currentTemplate),
        campaign?.variables
      )
    : null;

  return (
    <div className="min-h-screen bg-[#fff] p-5 lg:p-6">
      <div className="max-w-[1520px] mx-auto">
        {/* Page header — matches Campaigns / Retarget style (no duplicate sticky bar) */}
        <div className="mb-5">
          <button
            type="button"
            onClick={() => navigate("/campaigns")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors text-sm font-semibold"
          >
            <ArrowLeft size={18} />
            Back to Campaigns
          </button>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                {campaign?.campaign_name || "Campaign"}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Delivery analytics and contact breakdown
              </p>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border border-gray-300 bg-white text-gray-900 hover:shadow-md transition-all disabled:opacity-50 shrink-0"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {/* Campaign meta */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold uppercase border ${statusBadgeClass(campaign?.status)}`}
            >
              {campaign?.status || "N/A"}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-xs font-medium text-gray-700">
              <Users size={13} className="text-gray-500" />
              {(analytics?.total_contacts || 0).toLocaleString()} audience
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-xs font-medium text-gray-700">
              <Calendar size={13} className="text-gray-500" />
              {campaign?.created_at
                ? new Date(campaign.created_at).toLocaleDateString()
                : "—"}
            </span>
            {phone && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-xs font-medium text-gray-700 max-w-full">
                <Smartphone size={13} className="text-gray-500 shrink-0" />
                <span className="truncate">
                  {phone.display_name ? `${phone.display_name} · ` : ""}
                  {phone.phone_number}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Status tabs — pill style like Campaigns filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const isActive = activeTab === status;
            const count = getCount(status);
            const pct = getPercentage(status);
            const Icon = config.icon;

            return (
              <button
                key={status}
                type="button"
                onClick={() => handleTabClick(status)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-xl border transition-all shadow-sm ${
                  isActive
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-900 border-gray-300 hover:shadow-md"
                }`}
              >
                {status !== "overview" && (
                  <span className={`text-xs font-bold tabular-nums ${isActive ? "text-white/90" : "text-gray-500"}`}>
                    {pct}%
                  </span>
                )}
                <span
                  className={`text-[11px] px-1.5 py-0.5 rounded-md tabular-nums font-bold ${
                    isActive ? "bg-white/15 text-white" : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {count.toLocaleString()}
                </span>
                <Icon
                  size={14}
                  className={isActive ? "text-white" : config.textColor}
                />
                <span className="hidden sm:inline">
                  {status === "overview" ? "Overview" : config.label}
                </span>
              </button>
            );
          })}
        </div>

        {activeTab === "overview" ? (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(280px,320px)_1fr] gap-6 xl:gap-8 items-start">
            {/* Sidebar — template preview */}
            <aside className="space-y-5 xl:sticky xl:top-4">
              {phone && (
                <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="w-9 h-9 rounded-full bg-[#25D366]/10 flex items-center justify-center shrink-0">
                    <Smartphone size={16} className="text-[#128C7E]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {phone.display_name || "WhatsApp Business"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{phone.phone_number}</p>
                  </div>
                </div>
              )}

              {templates && templates.length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Template {activeTemplateIndex + 1}/{templates.length}
                    </p>
                    {templates.length > 1 && (
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => setActiveTemplateIndex((i) => Math.max(0, i - 1))}
                          disabled={activeTemplateIndex === 0}
                          className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-40"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setActiveTemplateIndex((i) =>
                              Math.min(templates.length - 1, i + 1)
                            )
                          }
                          disabled={activeTemplateIndex === templates.length - 1}
                          className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-40"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {currentTemplate?.name}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {currentTemplate?.language} · {currentTemplate?.category}
                    </p>
                  </div>
                  {previewState ? (
                    <TemplatePreview state={previewState} />
                  ) : (
                    <div className="p-6 bg-white rounded-xl border border-gray-200 text-center text-sm text-gray-500">
                      Preview not available
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 bg-white rounded-xl border border-gray-200 text-center text-sm text-gray-500">
                  No templates found
                </div>
              )}
            </aside>

            {/* Main analytics */}
            <div className="space-y-6 min-w-0">
              {/* Metric cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {metricCards.map((status) => {
                  const config = STATUS_CONFIG[status];
                  const count = getCount(status);
                  const pct = getPercentage(status);
                  const Icon = config.icon;
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleTabClick(status)}
                      className="group text-left p-5 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all shadow-sm"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <Icon size={16} className={config.textColor} />
                        <span className="text-[11px] font-semibold text-gray-400 tabular-nums">
                          {pct}%
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900 tabular-nums leading-none">
                        {count.toLocaleString()}
                      </p>
                      <p className="text-xs font-medium text-gray-600 mt-2">{config.label}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{config.subLabel}</p>
                    </button>
                  );
                })}
              </div>

              {/* Pending + total strip */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4 bg-white rounded-xl border border-gray-200 text-xs text-gray-600">
                <span className="font-medium text-gray-900">
                  {(analytics?.pending_contacts || 0).toLocaleString()} pending
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  {(analytics?.total_contacts || 0).toLocaleString()} total contacts
                </span>
                {(analytics?.unaccounted ?? 0) !== 0 && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-amber-700 font-medium">
                      Δ {(analytics?.unaccounted || 0).toLocaleString()} unaccounted
                    </span>
                  </>
                )}
              </div>

              {/* Chart */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-5">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Status over time</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      End-of-day bucket counts — same definitions as the cards above
                    </p>
                  </div>
                </div>
                <div className="h-[340px]">
                  {graphLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-gray-800 rounded-full" />
                    </div>
                  ) : dateWiseData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <BarChart3 size={40} className="mb-2 opacity-40" />
                      <p className="text-sm">No timeline data yet</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dateWiseData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="fSent" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="fDelivered" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="fRead" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="fReplied" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="fFailed" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                        <XAxis
                          dataKey="date"
                          stroke="#9ca3af"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(v) => {
                            const d = new Date(v);
                            return `${d.getDate()}/${d.getMonth() + 1}`;
                          }}
                        />
                        <YAxis
                          stroke="#9ca3af"
                          fontSize={11}
                          tickLine={false}
                          axisLine={false}
                          width={48}
                          tickFormatter={(v) =>
                            v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                          }
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }} />
                        <Area
                          type="monotone"
                          dataKey="failed"
                          stackId="status"
                          stroke="#ef4444"
                          strokeWidth={1.5}
                          fill="url(#fFailed)"
                          name="Failed"
                        />
                        <Area
                          type="monotone"
                          dataKey="replied"
                          stackId="status"
                          stroke="#f59e0b"
                          strokeWidth={1.5}
                          fill="url(#fReplied)"
                          name="Replied"
                        />
                        <Area
                          type="monotone"
                          dataKey="read"
                          stackId="status"
                          stroke="#8b5cf6"
                          strokeWidth={1.5}
                          fill="url(#fRead)"
                          name="Read"
                        />
                        <Area
                          type="monotone"
                          dataKey="delivered"
                          stackId="status"
                          stroke="#10b981"
                          strokeWidth={1.5}
                          fill="url(#fDelivered)"
                          name="Delivered"
                        />
                        <Area
                          type="monotone"
                          dataKey="sent"
                          stackId="status"
                          stroke="#3b82f6"
                          strokeWidth={1.5}
                          fill="url(#fSent)"
                          name="Sent"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            {/* Actions */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {React.createElement(STATUS_CONFIG[activeTab].icon, { size: 20, className: STATUS_CONFIG[activeTab].textColor })}
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{STATUS_CONFIG[activeTab].label} Contacts</h3>
                  <p className="text-sm text-gray-500">{pagination.total.toLocaleString()} contacts</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExport}
                  disabled={exportLoading || pagination.total === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  {exportLoading ? (
                    <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-gray-700 rounded-full" />
                  ) : (
                    <Download size={16} />
                  )}
                  Export CSV
                </button>
                <button
                  onClick={handleRetarget}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors"
                >
                  <Target size={16} />
                  Retarget
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {contactsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-gray-700 rounded-full" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <Users size={48} className="mb-2 opacity-50" />
                  <p className="text-sm">No contacts found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Phone</th>
                      {activeTab === "sent" && (
                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Sent At</th>
                      )}
                      {activeTab === "delivered" && (
                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Delivered At</th>
                      )}
                      {activeTab === "read" && (
                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Read At</th>
                      )}
                      {activeTab === "replied" && (
                        <>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Customer Name</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Reply</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Reply At</th>
                        </>
                      )}
                      {activeTab === "failed" && (
                        <>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Failed Reason</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Failed At</th>
                        </>
                      )}
                      {activeTab === "pending" && (
                        <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Variables</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contacts.map((c, i) => {
                      // Format date helper
                      const formatDate = (dateString) => {
                        if (!dateString) return "-";
                        try {
                          const date = new Date(dateString);
                          return date.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                        } catch (e) {
                          return dateString;
                        }
                      };

                      return (
                        <tr key={c.contact_id || i} className="hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2 text-sm text-gray-900 font-medium">
                              <Phone size={14} className="text-gray-400" />
                              {c.phone || "-"}
                            </div>
                          </td>
                          
                          {activeTab === "sent" && (
                            <td className="py-3 px-4">
                              <span className="text-sm text-gray-600">{formatDate(c.sent_at)}</span>
                            </td>
                          )}
                          
                          {activeTab === "delivered" && (
                            <td className="py-3 px-4">
                              <span className="text-sm text-gray-600">{formatDate(c.delivered_at)}</span>
                            </td>
                          )}
                          
                          {activeTab === "read" && (
                            <td className="py-3 px-4">
                              <span className="text-sm text-gray-600">{formatDate(c.read_at)}</span>
                            </td>
                          )}
                          
                          {activeTab === "replied" && (
                            <>
                              <td className="py-3 px-4">
                                <span className="text-sm text-gray-900 font-medium">{c.customer_name || "-"}</span>
                              </td>
                              <td className="py-3 px-4 max-w-md">
                                <p className="text-sm text-gray-700 truncate" title={c.reply || ""}>
                                  {c.reply || "-"}
                                </p>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm text-gray-600">{formatDate(c.reply_at)}</span>
                              </td>
                            </>
                          )}
                          
                          {activeTab === "failed" && (
                            <>
                              <td className="py-3 px-4 max-w-md">
                                <p className="text-sm text-red-600 truncate" title={c.failed_reason || ""}>
                                  {c.failed_reason || "-"}
                                </p>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-sm text-gray-600">{formatDate(c.failed_at)}</span>
                              </td>
                            </>
                          )}
                          
                          {activeTab === "pending" && (
                            <td className="py-3 px-4">
                              {c.variables && Object.keys(c.variables).length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(c.variables).map(([key, value]) => (
                                    <span
                                      key={key}
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                                      title={`${key}: ${value}`}
                                    >
                                      <span className="font-medium">{key}:</span>
                                      <span className="truncate max-w-[100px]">{String(value)}</span>
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">-</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()}
                </p>
                <div className="flex items-center gap-1">
                  <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50">
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let p;
                    if (pagination.totalPages <= 5) p = i + 1;
                    else if (pagination.page <= 3) p = i + 1;
                    else if (pagination.page >= pagination.totalPages - 2) p = pagination.totalPages - 4 + i;
                    else p = pagination.page - 2 + i;
                    return (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium ${pagination.page === p ? "bg-gray-900 text-white" : "hover:bg-gray-100"}`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50">
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignAnalytics;
